import type { BotConfig, Template } from './types';

export const HEBREW_TEMPLATE_NAME = 'Hebrew Voice Agent';

// One allowlist for applying and saving templates across the console.
export const TEMPLATE_FIELDS = [
  'prompt_base', 'prompt_extras', 'welcome_message', 'bot_goes_first', 'bot_goes_first_outbound',
  'emotions_yaml', 'llm_provider', 'llm_model', 'openai_realtime_model',
  'llm_provider_priority_2', 'llm_model_priority_2', 'openai_realtime_model_priority_2',
  'voice_gender', 'openai_temperature', 'openai_temperature_priority_2', 'fillers_enabled', 'filler_delay_ms',
  'sound_effects_filler_enabled', 'affirmations_enabled', 'hold_enabled', 'lock_lookups_to_caller_id',
  'bvc_enabled', 'bvc_enhancement_level_web', 'bvc_enhancement_level_telephony',
  'office_ambience_enabled', 'office_ambience_volume', 'office_ambience_mode',
  'tts_reverb_enabled', 'tts_reverb_wet', 'adaptive_prebuffer_enabled', 'auto_quality_stepdown_enabled',
  'workflow_mode', 'deepdub_model', 'audio_sample_rate', 'gemini_tts_prompt',
  'hold_music_enabled', 'hold_music_track', 'hold_music_volume',
  'supported_languages', 'default_locale', 'default_country', 'timezone',
  'eot_hold_dynamic', 'eot_hold_alpha', 'current_time_tool_enabled',
  'save_config_history_enabled', 'require_publish_before_save',
] as const satisfies readonly (keyof BotConfig)[];

export function templatePatch(template: Template): Partial<BotConfig> {
  const patch: Record<string, unknown> = {};
  for (const field of TEMPLATE_FIELDS) {
    const value = (template as unknown as Record<string, unknown>)[field];
    if (value !== undefined && value !== null) patch[field] = value;
  }
  return patch as Partial<BotConfig>;
}

export function isHebrewTemplate(template: Template): boolean {
  return template.name === HEBREW_TEMPLATE_NAME && template.is_default === true;
}

export function orderTemplates(templates: Template[]): Template[] {
  return [...templates].sort((a, b) => Number(isHebrewTemplate(b)) - Number(isHebrewTemplate(a)));
}

export interface HebrewSetting {
  id: keyof BotConfig;
  label: string;
  group: string;
  fields: readonly (keyof BotConfig)[];
  value: string;
  preview?: string;
}

/** Semantic properties: a model/voice's wire fields are selected together. */
export function hebrewSettings(template: Template): HebrewSetting[] {
  const patch = templatePatch(template);
  const definitions: HebrewSetting[] = [
    { id: 'deepdub_model', label: 'Speech model', value: '3.4 · phantomz-heb', fields: ['deepdub_model'], group: 'Voice & intelligence' },
    { id: 'emotions_yaml', label: 'Voice', value: 'Tal Katz', fields: ['emotions_yaml', 'voice_gender'], group: 'Voice & intelligence' },
    { id: 'llm_model', label: 'Conversation model', value: 'GPT Realtime', fields: ['llm_provider', 'llm_model', 'openai_realtime_model'], group: 'Voice & intelligence' },
    { id: 'llm_model_priority_2', label: 'Fallback conversation model', value: 'Gemini 3.1 Flash Lite · used if the primary model is unavailable', fields: ['llm_provider_priority_2', 'llm_model_priority_2', 'openai_realtime_model_priority_2'], group: 'Voice & intelligence' },
    { id: 'fillers_enabled', label: 'Fillers', value: 'Enabled', fields: ['fillers_enabled'], group: 'Natural conversation' },
    { id: 'filler_delay_ms', label: 'Filler delay', value: `${patch.filler_delay_ms} ms`, fields: ['filler_delay_ms'], group: 'Natural conversation' },
    { id: 'affirmations_enabled', label: 'Affirmations', value: 'Enabled', fields: ['affirmations_enabled'], group: 'Natural conversation' },
    { id: 'bot_goes_first', label: 'Greet first on inbound calls', value: 'Enabled', fields: ['bot_goes_first'], group: 'Natural conversation' },
    { id: 'eot_hold_dynamic', label: 'Dynamic end-of-turn hold', value: 'Enabled', fields: ['eot_hold_dynamic'], group: 'Natural conversation' },
    { id: 'eot_hold_alpha', label: 'Starting sensitivity (α)', value: String(patch.eot_hold_alpha), fields: ['eot_hold_alpha'], group: 'Natural conversation' },
    { id: 'bvc_enabled', label: 'Voice cleaning', value: 'Enabled', fields: ['bvc_enabled'], group: 'Audio clarity' },
    { id: 'bvc_enhancement_level_telephony', label: 'Telephony cleaning strength', value: String(patch.bvc_enhancement_level_telephony), fields: ['bvc_enhancement_level_telephony'], group: 'Audio clarity' },
    { id: 'bvc_enhancement_level_web', label: 'Browser mic cleaning strength', value: String(patch.bvc_enhancement_level_web), fields: ['bvc_enhancement_level_web'], group: 'Audio clarity' },
    { id: 'default_locale', label: 'Default language', value: 'Hebrew (he-IL)', fields: ['default_locale'], group: 'Language & tools' },
    { id: 'supported_languages', label: 'Supported languages', value: 'Hebrew', fields: ['supported_languages'], group: 'Language & tools' },
    { id: 'default_country', label: 'Target country', value: 'Israel', fields: ['default_country'], group: 'Language & tools' },
    { id: 'timezone', label: 'Time zone', value: 'Asia/Jerusalem', fields: ['timezone'], group: 'Language & tools' },
    { id: 'current_time_tool_enabled', label: 'Check the current time', value: 'Enabled', fields: ['current_time_tool_enabled'], group: 'Language & tools' },
    { id: 'save_config_history_enabled', label: 'Save config history', value: 'Enabled', fields: ['save_config_history_enabled'], group: 'Publishing' },
    { id: 'require_publish_before_save', label: 'Require explicit Publish', value: 'Enabled', fields: ['require_publish_before_save'], group: 'Publishing' },
    { id: 'welcome_message', label: 'Welcome message', value: patch.welcome_message || '', fields: ['welcome_message'], group: 'Prompt & greeting' },
    { id: 'prompt_base', label: 'System prompt', value: 'Hebrew business-agent starter', preview: patch.prompt_base, fields: ['prompt_base'], group: 'Prompt & greeting' },
  ];
  return definitions.filter(setting => setting.fields.some(field => patch[field] !== undefined));
}

/** The normal empty fallback inherits GPT Realtime, which the template uses as primary. */
export function needsHebrewFallbackChange(template: Template, config?: Partial<BotConfig>): boolean {
  if ((config?.llm_provider_priority_2?.trim() || 'openai') !== template.llm_provider) return false;
  const primaryModel = template.openai_realtime_model || template.llm_model || '';
  const fallbackModel = config?.openai_realtime_model_priority_2?.trim() || primaryModel;
  return primaryModel === fallbackModel;
}

/** Check the proposed combination, including keeping either of the current models. */
export function hebrewModelSelectionConflict(template: Template, selected: ReadonlySet<string>, config?: Partial<BotConfig>): boolean {
  if (!selected.has('llm_model') && !selected.has('llm_model_priority_2')) return false;
  const next = { ...config, ...selectedHebrewPatch(template, selected, config) };
  const primaryProvider = next.llm_provider?.trim() || 'gemini';
  const fallbackProvider = next.llm_provider_priority_2?.trim() || 'openai';
  if (primaryProvider !== fallbackProvider) return false;
  if (primaryProvider === 'openai') {
    const defaultModel = template.openai_realtime_model || template.llm_model || '';
    return (next.openai_realtime_model?.trim() || defaultModel) === (next.openai_realtime_model_priority_2?.trim() || defaultModel);
  }
  const defaultModel = primaryProvider === template.llm_provider_priority_2 ? template.llm_model_priority_2 || '' : '';
  return (next.llm_model?.trim() || defaultModel) === (next.llm_model_priority_2?.trim() || defaultModel);
}

export function selectedHebrewPatch(template: Template, selected: ReadonlySet<string>, config?: Partial<BotConfig>): Partial<BotConfig> {
  const patch = templatePatch(template);
  const result: Record<string, unknown> = {};
  for (const setting of hebrewSettings(template)) {
    if (!selected.has(setting.id)) continue;
    for (const field of setting.fields) if (patch[field] !== undefined) result[field] = patch[field];
  }
  // Multi-persona calls resolve the default persona's voice before top-level
  // emotions. Keep that voice in sync while preserving roles and other voices.
  if (selected.has('emotions_yaml') && patch.emotions_yaml && config?.personas?.length) {
    const defaultIndex = Math.max(0, config.personas.findIndex(persona => persona.is_default));
    result.personas = config.personas.map((persona, index) => index === defaultIndex ? {
      ...persona,
      voice_preset: 'tal_katz',
      emotions_yaml: patch.emotions_yaml!,
      ...(patch.voice_gender !== undefined ? { voice_gender: patch.voice_gender } : {}),
    } : persona);
  }
  return result as Partial<BotConfig>;
}
